import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOn';
import { ReactComponent as Url } from '../../assets/images/url.svg';
import PhoneOutlinedIcon from '@mui/icons-material/Phone';
import EmailOutlinedIcon from '@mui/icons-material/Email';
import { useDispatch, useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import DatePicker from '../commonComponents/DatePicker';
import moment from 'moment';
import { COUNTRY_NAMES, BUSINESS_MODEL_MAP, SUPPORT_FLOW_MAP } from '../../utils/constants';
import {
  formatDate,
  formatBoolean,
  formatFullName,
  formatWebsiteUrl,
  getDisplayValue,
  formatSupportFlow,
} from '../../utils/formatters';
import { getRegistrationData, saveImplementationData } from '../../redux/slices/sellerSlice';
import DeleteSellerPopUp from './deleteSellerPopUp';
import CommonTextField from './editForm/CommonTextField';
import CommonDropdown from './editForm/CommonDropdown';
import PosModal from './editForm/PosModal';
import FieldError from './editForm/FieldError';
import { validateForm, validateField } from '../../utils';
import deleteIcon from '../../assets/images/deleteIcon.svg'
import locationIcon from '../../assets/images/locationIcon.svg'
import calendarIcon from '../../assets/images/calendar.svg'

// ============================================================
// Reusable Sub-Components
// ============================================================

// Section wrapper with consistent title styling
const SectionWrapper = ({ title, children }) => {
  return (
    <Box sx={{ marginBottom: '32px', '&:last-child': { marginBottom: 0 } }}>
      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#000000', marginBottom: '16px' }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
};

// Field display component with label and value (handles null/undefined with "-")
const FieldDisplay = ({ label, value, icon }) => {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>}
        <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#6D6D6F', marginBottom: '4px' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#000000' }} component="div">
        {value || '-'}
      </Typography>
    </Box>
  );
};

// Individual contact card component
const ContactCard = ({ title, name, phone, email }) => {
  return (
    <Box>
      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#000000', marginBottom: '16px' }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#000000', marginBottom: '12px' }}>
        {name}
      </Typography>
      {/* Phone */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <PhoneOutlinedIcon sx={{ color: '#000000', fontSize: '18px' }} />
        <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#000000' }}>
          {getDisplayValue(phone)}
        </Typography>
      </Box>
      {/* Email */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <EmailOutlinedIcon sx={{ color: '#000000', fontSize: '18px' }} />
        <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#000000' }}>
          {getDisplayValue(email)}
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================
// Flag helper for POS implementation
// ============================================================

const getFlag = (code) => `/flags/${code === 'UK' ? 'GB' : code}.png`;

// ============================================================
// Helper: Parse POS codes from seller data
// ============================================================

const parsePosCodes = (pos_code, supList = [] ) => {
  if (!pos_code) return [];
  const supportedSet = new Set((supList || []).map((s) => s.pos_code));
  const isSupported = (code) => !supportedSet.size || supportedSet.has(code);

  try {
    if (pos_code.includes('{')) {
      const fixed = `[${pos_code}]`;
      const parsed = JSON.parse(fixed);
      return parsed.filter(item => item && (item.id || item.iata)).filter(item => isSupported(item.id));;
    }
    return pos_code.split(',').map(code => code.trim()).filter(code => isSupported(code));
  } catch (err) {
    console.error('Error parsing pos_code:', err);
    return [];
  }
};

const parseBusinessModel = (business_model, modelMap = BUSINESS_MODEL_MAP) => {
  if (!business_model) return null;
  const labelFor = (id) => modelMap?.[id] || BUSINESS_MODEL_MAP[id] || id;
  try {
    if (business_model.includes('{')) {
      const fixed = `[${business_model}]`;
      const parsed = JSON.parse(fixed);
      return parsed
        .map(item => labelFor(item?.id))
        .filter(Boolean)
        .join(', ') || null;
    }
    return labelFor(business_model);
  } catch (err) {
    return business_model;
  }
};

// ============================================================
// Edit-form helpers
// ============================================================

// Flags live in /public/flags as 2-letter codes (UK uses GB asset).
const editFlagSrc = (code) =>
  code && code !== 'OT' ? `/flags/${code === 'UK' ? 'GB' : code}.png` : '';

// Parse seller.pos_code -> [{ code, name, flag, iata }] (pre-fills the POS modal)
const parsePosItems = (pos_code, supList = [], defaultIata = '') => {
  const nameOf = (code) =>
    supList.find((s) => s.pos_code === code)?.country ||
    COUNTRY_NAMES[code] ||
    code;
  return parsePosCodes(pos_code,supList)
    .map((pos) => {
      const code = typeof pos === 'string' ? pos : pos?.id;
      if (!code) return null;
      const iata = typeof pos === 'string' ? defaultIata || '' : pos?.iata || defaultIata || '';
      return { code, name: nameOf(code), iata: iata || '' };
    })
    .filter(Boolean);
};

// Parse seller.business_model JSON string -> { id, iata }
const parseBusinessModelValue = (business_model) => {
  if (!business_model) return [{ id: '', iata: null }];

  const trimmed = String(business_model).trim();

  // Object-list form
  if (trimmed.includes('{')) {
    try {
      const obj = JSON.parse(`[${trimmed}]`);
      return Array.isArray(obj) && obj.length ? obj : [{ id: '', iata: null }];
    } catch (err) {
      console.log(err);
      return [{ id: '', iata: null }];
    }
  }

  // Plain CSV form
  return trimmed
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => ({ id:id, iata: null }));
};



// Empty edit form shape
const EMPTY_EDIT_FORM = {
  company_name: '',
  supported_by_IT_team: 0,
  existing_integration: 0,
  api_schema_version: [],
  pos_items: [],
  company_address: '',
  company_website: '',
  customer_type_id: "",
  business_model: [],
  booking_volumes: '',
  sales_mix_domestic: '',
  sales_mix_transborder: '',
  sales_mix_international: '',
  implementation_start_date: null,
  supported_flow: [],
  expected_tps: '',
  existing_integration_comments: '',
  business_contact_first_name: '',
  business_contact_last_name: '',
  business_email: '',
  business_phone_no: '',
  admin_contact_first_name: '',
  admin_contact_last_name: '',
  admin_contact_email: '',
  admin_contact_phone: '',
  sameAsBusiness: false,
};

// Section heading for the edit form (weight 700 / 18px / mb 14px)
const EditSectionTitle = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 700,
      fontSize: '18px',
      letterSpacing: 0,
      color: '#000000',
      mb: '15px',
    }}
  >
    {children}
  </Typography>
);

// ============================================================
// Main Profile Tab Component
//
// Sections:
// 1. Commercial details (address, website, business model, customer type, POS)
// 2. Technical details (implementation date, support flow, API schema, existing integration)
// 3. Contact information (Business contact, Admin contact)
// 4. Action buttons (Delete, Edit)
// ============================================================

const SellerProfile = ({ selectedSeller, formEdit, setFormEdit, companyNameEdit, setCompanyNameEdit }) => {
  const seller = selectedSeller;
  const dispatch = useDispatch();

  const [deletePopUpOpen, setDeletePopUpOpen] = React.useState(false);
  const [openDatePicker, setOpenDatePicker] = React.useState(false);

  const state = useSelector(({ seller: { registrationData } }) => ({
    registrationData: registrationData || {},
  }));

  const [formData, setFormData] = React.useState(EMPTY_EDIT_FORM);
  const [errors, setErrors] = React.useState({});
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [posModalOpen, setPosModalOpen] = React.useState(false);

  // Initialize formData from seller when entering edit mode.
  // Mirrors the seller GET object (pos_code / business_model parsed from JSON).
  React.useEffect(() => {
    if (formEdit && seller) {
      const bm = parseBusinessModelValue(seller?.business_model);
      console.log(bm)
      const num = (v) => (v === null || v === undefined ? "" : Number(v));
      // Ignore stored customer types that aren't valid dropdown options so the
      // field reads as empty and the required-field validation is enforced.
      const customerTypes = state.registrationData?.customer_types || [];
      const isValidCustomerType = customerTypes.some(
        (c) => c.customer_type_id === seller?.customer_type_id
      );
      setFormData({
        // preserved (not edited in the form, but sent back unchanged)
        company_name: companyNameEdit || seller?.company_name || "",
        // read-only displays (still sent in payload)
        supported_by_IT_team:
          seller?.supported_by_IT_team ?? seller?.supported_by_IT ?? 0,
        existing_integration: seller?.existing_integration ?? 0,
        // Technical details: API schema + POS implementation
        api_schema_version: seller?.api_schema_version?.split(",").map(v => v.trim()) || [],
        pos_items: parsePosItems(seller?.pos_code, state.registrationData?.sup, seller?.iata_number),
        // Commercial details
        company_address: seller?.company_address || "",
        company_website: seller?.company_website || "",
        // Commercial details: business model + customer type
        customer_type_id: isValidCustomerType ? seller.customer_type_id : "",
        business_model: bm.map(b => b.id) || [],
        booking_volumes: seller?.booking_volumes || null,
        sales_mix_domestic: num(seller?.sales_mix_domestic),
        sales_mix_transborder: num(seller?.sales_mix_transborder),
        sales_mix_international: num(seller?.sales_mix_international),
        // Technical details
        implementation_start_date: moment(seller.implementation_start_date).toISOString() || null,
        supported_flow: seller?.supported_flow || [],
        expected_tps: seller?.expected_tps || "",
        existing_integration_comments: seller?.existing_integration_comments || "",
        // Contacts
        business_contact_first_name: seller?.business_contact_first_name || "",
        business_contact_last_name: seller?.business_contact_last_name || "",
        business_email: seller?.business_email || "",
        business_phone_no: seller?.business_phone_no || "",
        admin_contact_first_name: seller?.admin_contact_first_name || "",
        admin_contact_last_name: seller?.admin_contact_last_name || "",
        admin_contact_email: seller?.admin_contact_email || "",
        admin_contact_phone: seller?.admin_contact_phone || "",
        sameAsBusiness: false,
      });
      setErrors({});
      setSubmitAttempted(false);
    }
  }, [formEdit, seller, state.registrationData?.sup, state.registrationData?.customer_types, companyNameEdit]);

  // Load registration data on mount
  React.useEffect(() => {
    dispatch(getRegistrationData());
  }, [dispatch]);

  // ─── Dropdown options derived from registration data / constants ────────────
  const alphaSort = (left, right) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' });

  const companyTypeOptions = [...(state.registrationData?.customer_types || [])]
    .sort((a, b) => {
      if (a.customer_type === "All") return -1;
      if (b.customer_type === "All") return 1;
      if (a.customer_type === "Both") return 1;
      if (b.customer_type === "Both") return -1;
      return alphaSort(a.customer_type, b.customer_type);
    })
    .map((c) => ({ value: c.customer_type_id, label: c.customer_type }));

  const companyTypeMap = (state.registrationData?.customer_types || []).reduce(
    (acc, customerType) => {
      acc[customerType.customer_type_id] = customerType.customer_type;
      return acc;
    },
    {}
  );

  const businessModelOptions = [...(state.registrationData?.business_model || [])]
    .sort((a, b) => {
      if (a.business_model_type === "Other") return 1;
      if (b.business_model_type === "Other") return -1;
      return alphaSort(a.business_model_type, b.business_model_type);
    })
    .map((b) => ({ value: b.business_model_id, label: b.business_model_type }));
  
  // Map business_model_id -> business_model_type from registration data (for display)
  const businessModelMap = (state.registrationData?.business_model || []).reduce(
    (acc, b) => {
      acc[b.business_model_id] = b.business_model_type;
      return acc;
    },
    {}
  );  

  const apiSchemaOptions = [...(state.registrationData?.api_schema || [])].map(
    (a) => ({
      value: a.api_schema_version,
      label: a.api_schema_version === "AS001" ? "Other" : a.api_schema_version,
    })
  );

  const supportedFlowOptions = Object.entries(SUPPORT_FLOW_MAP).map(
    ([value, label]) => ({ value, label })
  );

  const isIataEnabledForPos = formData.business_model.includes('AG');

  // ─── Field change helpers ───────────────────────────────────────────────────
  const setField = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'business_model' && !value.includes('AG')) {
        next.pos_items = prev.pos_items.map((pos) => ({ ...pos, iata: '' }));
      }

      // Keep admin contact mirrored while "same as business" is checked
      if (
        prev.sameAsBusiness &&
        ["business_contact_first_name", "business_contact_last_name", "business_email", "business_phone_no"].includes(name)
      ) {
        if (name === "business_contact_first_name") next.admin_contact_first_name = value;
        if (name === "business_contact_last_name") next.admin_contact_last_name = value;
        if (name === "business_email") next.admin_contact_email = value;
        if (name === "business_phone_no") next.admin_contact_phone = value;
      }

      return next;
    });

    if (submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value, {
          existingIntegration: formData.existing_integration === 1,
        }),
      }));
    }
  };

  const handleSameAsBusiness = (checked) => {
    setFormData((prev) => ({
      ...prev,
      sameAsBusiness: checked,
      ...(checked && {
        admin_contact_first_name: prev.business_contact_first_name,
        admin_contact_last_name: prev.business_contact_last_name,
        admin_contact_email: prev.business_email,
        admin_contact_phone: prev.business_phone_no,
      }),
    }));
  };

  const handlePosApply = (items) => {
    // Normalize modal output (keyed by `id`) to the `code` shape used everywhere else.
    const normalized = items.map((it) => ({
      code: it.code ?? it.id,
      name: it.name,
      iata: it.iata || "",
      flag: it.flag,
    }));
    setField("pos_items", normalized);
    setPosModalOpen(false);
  };

  const handleEditFormSubmit = () => {
    setSubmitAttempted(true);
    // Block submission when the company name is empty
    if (!companyNameEdit?.trim()) {
      return;
    }
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    // Payload mirrors the seller GET object (flat snake_case + JSON-string fields)
    const data = {
      company_name: companyNameEdit,
      company_address: formData.company_address,
      company_website: formData.company_website || null,
      customer_type_id: formData.customer_type_id,
      business_model_type: formData.business_model.map(b => ({ "id": b, "iata": null })),
      api_schema_version: formData.api_schema_version,
      pos_preference: formData.pos_items.map(p => ({ "id": p.code||p.id||null, "iata": isIataEnabledForPos && Number(p.iata) ? Number(p.iata) : null })),
      support_flow: [formData.supported_flow],
      implementation_start_date: formData.implementation_start_date
        ? moment(formData.implementation_start_date).format('YYYY-MM-DD')
        : null,
      existing_integration: Boolean(formData.existing_integration),
      existing_integration_comments:
        formData.existing_integration === 1
          ? formData.existing_integration_comments || null
          : null,
      supported_by_IT: Boolean(formData.supported_by_IT_team)?Boolean(formData.supported_by_IT_team):true,
      business_contact_first_name: formData.business_contact_first_name,
      business_contact_last_name: formData.business_contact_last_name,
      business_email: formData.business_email,
      business_phone_no: formData.business_phone_no || null,
      admin_contact_first_name: formData.admin_contact_first_name,
      admin_contact_last_name: formData.admin_contact_last_name,
      admin_contact_email: formData.admin_contact_email,
      admin_contact_phone: formData.admin_contact_phone || null,
    };

    dispatch(saveImplementationData({ data }));
    setFormEdit(false);
    setCompanyNameEdit(formData.company_name);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (

    <>
      {!formEdit ? (
        <Box
          sx={{
            marginTop: "10px",
            maxWidth: '1200px',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
            backgroundColor: '#fff',
            mb: "24px",
          }}
        >

          {/* ============================== */}
          {/* Section 1: Commercial Details */}
          {/* Company address, website, business model, customer type, POS implementation */}
          {/* ============================== */}
          <SectionWrapper title="Commercial details">
            <Grid container spacing={3}>
              {/* Company Address */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LocationOnOutlinedIcon sx={{ color: '#000000', fontSize: '20px', marginTop: '2px' }} />
                  <Box sx={{ flex: 1,  }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#6D6D6F', marginBottom: '4px' }}>
                      Company address
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#000000' }}>
                      {getDisplayValue(seller?.company_address)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Company Website */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Url sx={{ color: '#000000', fontSize: '20px', marginTop: '2px' }} />
                  <Box sx={{ flex: 1,}}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#6D6D6F', marginBottom: '4px' }}>
                      Company website
                    </Typography>
                    {seller?.company_website && seller?.company_website.trim() !== '' ? (
                      <Link
                        href={seller?.company_website.startsWith('http')
                          ? seller?.company_website
                          : `https://${seller?.company_website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: '16px',
                          fontWeight: 500,
                          color: '#1460AA',
                          textDecoration: 'underline',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {formatWebsiteUrl(seller?.company_website)}
                      </Link>
                    ) : (
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#000000' }}>-</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Business model */}
              <Grid item xs={12} md={6}>
                <FieldDisplay
                  label="Business model"
                  value={getDisplayValue(parseBusinessModel(seller?.business_model, businessModelMap))}
                />
              </Grid>

              {/* Who are your customers? */}
              <Grid item xs={12} md={6}>
                <FieldDisplay
                  label="Who are your customers?"
                  value={getDisplayValue(companyTypeMap[seller?.customer_type_id] || seller?.customer_type)}
                />
              </Grid>

              {/* POS Implementation */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#666666', marginBottom: '4px' }}>
                  POS implementation
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginTop: '8px' }}>
                  {parsePosCodes(seller?.pos_code, state.registrationData?.sup).length > 0 ? (
                    parsePosCodes(seller?.pos_code, state.registrationData?.sup).map((pos) => {
                      const flagSrc = typeof pos === "string"
                        ? getFlag(pos)
                        : getFlag(pos?.id);
                      const isOther = pos === "OT" || pos?.id === "OT";
                      return (
                        <Box
                          key={`${pos.id || pos}`}
                          sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px' }}
                        >
                          {!isOther && <img src={flagSrc} alt={typeof pos === "string" ? COUNTRY_NAMES[pos] : COUNTRY_NAMES[pos?.id]} width="24" height="16" />}
                          <Box>
                            <Typography sx={{ fontSize: '12px', fontWeight: 400, color: '#666666' }}>
                              {COUNTRY_NAMES[pos.id] || pos.id || COUNTRY_NAMES[pos]}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1a1a1a', justifySelf: !(seller?.iata_number || pos.iata) ? 'center' : 'start' }}>
                              {seller?.iata_number || pos.iata || '-'}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1a1a1a' }}>-</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </SectionWrapper>

          <Divider sx={{ marginY: '24px', borderColor: '#E0E0E0' }} />

          {/* ============================== */}
          {/* Section 2: Technical Details */}
          {/* Implementation date, supported flow, API schema, existing NDC integration */}
          {/* ============================== */}
          <SectionWrapper title="Technical details">
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <FieldDisplay
                  label="Implementation start date"
                  value={formatDate(seller?.implementation_start_date)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FieldDisplay
                  label="Supported flow"
                  value={formatSupportFlow(SUPPORT_FLOW_MAP[seller?.supported_flow])}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FieldDisplay
                  label="API schema"
                  value={getDisplayValue(seller?.api_schema_version?.split(',').reduce((acc, raw) => {
                    const t = raw.trim();
                    const normalized = ['17.2', '25.1', 'AS001'].includes(t) ? t : 'AS001';
                    const display = normalized === 'AS001' ? 'Other' : normalized;
                    if (!acc.includes(display)) acc.push(display);
                    return acc;
                  }, []).join(', '))}
                />
              </Grid>

              {/* Existing integration */}
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#6D6D6F', marginBottom: '4px' }}>
                    Do you have any existing NDC integration with another Airline?
                  </Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#000000' }}>
                    {formatBoolean(seller?.existing_integration)}
                  </Typography>
                  {/* Show integration comments/airline name if exists (existing_integration may come back as 0/1 or true/false) */}
                  {seller?.existing_integration_comments && Boolean(seller?.existing_integration) && (
                    <Box sx={{ marginTop: '8px' }}>
                      <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#6D6D6F', marginBottom: '4px' }}>
                        Please specify
                      </Typography>
                      <Typography sx={{ fontSize:'16px', fontWeight: 400, color: '#000000' }}>
                        {seller?.existing_integration_comments}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </SectionWrapper>

          <Divider sx={{ marginY: '24px', borderColor: '#E0E0E0' }} />

          {/* ============================== */}
          {/* Section 3: Contact Information */}
          {/* Shows Business contact and Admin contact side by side */}
          {/* ============================== */}
          <Grid container spacing={4}>
            {/* Business Contact */}
            <Grid item xs={12} md={6}>
              <ContactCard
                title="Business contact"
                name={formatFullName(seller?.business_contact_first_name, seller?.business_contact_last_name)}
                phone={seller?.business_phone_no}
                email={seller?.business_email}
              />
            </Grid>

            {/* Admin Contact */}
            <Grid item xs={12} md={6}>
              <ContactCard
                title="Admin contact"
                name={seller ? formatFullName(seller?.admin_contact_first_name, seller?.admin_contact_last_name) : '-'}
                phone={seller?.admin_contact_phone || null}
                email={seller?.admin_contact_email || null}
              />
            </Grid>
          </Grid>

          {/* ============================== */}
          {/* Section 4: Action Buttons */}
          {/* Delete and Edit buttons for seller profile management */}
          {/* ============================== */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '32px',
            }}
          >
            {/* Delete Button */}
            <Button
              variant="outlined"
              startIcon={<img src={deleteIcon} alt=""/>}
              onClick={() => setDeletePopUpOpen(true)}
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '24px',
                textTransform: 'none',
                padding: '8px 24px',
                borderColor: '#1460AA',
                color: '#1460AA',
              }}
            >
              Delete
            </Button>

            {/* Edit Button */}
            <Button
              variant="contained"
              startIcon={<EditIcon sx={{width: '12x', height: '12px'}}/>}
              onClick={() => { setFormEdit(true) }}
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '24px',
                textTransform: 'none',
                padding: '8px 24px',
                backgroundColor: '#1460AA',
                '& .MuiButton-startIcon': {
                  marginRight: '4px',
                },
                '&:hover': {
                  backgroundColor: '#1460AA',
                },
              }}
            >
              Edit
            </Button>
          </Box>
        </Box>

      ) : (
        <Box
          sx={{
            marginTop: "10px",
            maxWidth: "1200px",
            borderRadius: "16px",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            backgroundColor: "#FFFFFF",
            boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.15)",
            mb: "24px",
          }}
        >
          {/* ============================== */}
          {/* Section 1: Commercial details */}
          {/* ============================== */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Box sx={{display:'flex',justifyContent:'space-between', pt:'8px' }}>
            <EditSectionTitle>Commercial details</EditSectionTitle>
            <Typography sx={{ mb: '14px',fontWeight:400, fontSize:'16px', fontStyle:'italic', color:'#6D6D6F' }}><Box component="span" sx={{ color: "#D32F2F", mr: "4px" }}>*</Box>indicates a required field.</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <CommonTextField
                  label="Company address"
                  required
                  value={formData.company_address}
                  onChange={(v) => setField("company_address", v)}
                  startIcon={<LocationOnOutlinedIcon sx={{ fontSize: 20 }} />}
                  error={Boolean(errors.company_address)}
                  errorMessage={errors.company_address}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CommonTextField
                  label="Company website"
                  value={formData.company_website}
                  onChange={(v) => setField("company_website", v)}
                  startIcon={<img src={locationIcon} alt="" sx={{ fontSize: 20 }} />}
                  error={Boolean(errors.company_website)}
                  errorMessage={errors.company_website}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CommonDropdown
                  label="Business model"
                  required
                  value={formData.business_model}
                  multiple={true}
                  onChange={(v) => setField("business_model", v)}
                  options={businessModelOptions}
                  error={Boolean(errors.business_model)}
                  errorMessage={errors.business_model}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CommonDropdown
                  label="Who are your customers?"
                  required
                  value={formData.customer_type_id}
                  onChange={(v) => setField("customer_type_id", v)}
                  options={companyTypeOptions}
                  error={Boolean(errors.customer_type_id)}
                  errorMessage={errors.customer_type_id}
                />
              </Grid>

              {/* POS implementation */}
              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 1 }}>
                  <Typography
                    sx={{ color: "#6D6D6F", fontSize: "12px", lineHeight: "16px" }}
                  >
                    POS implementation
                    <Box component="span" sx={{ color: "#D32F2F", ml: "2px" }}>
                      *
                    </Box>
                  </Typography>
                  <Button
                    onClick={() => setPosModalOpen(true)}
                    startIcon={<EditIcon sx={{ fontSize: 16, width: '16px', height: '16px' }} />}
                    sx={{
                      textTransform: "none",
                      color: "#1460AA",
                      fontWeight: 700,
                      p: 0,
                      minWidth: 0,
                      textDecoration: 'underline',
                      "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                    }}
                    
                  >
                    Edit POS
                  </Button>
                </Stack>
                {formData.pos_items.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
                    {formData.pos_items.map((pos) => (
                      <Stack
                        key={pos.code}
                        direction="row"
                        alignItems="center"
                        gap={1}
                        sx={{ minWidth: "120px" }}
                      >
                        {pos.code !== "OT" && (
                          <img
                            key={pos.code}
                            src={
                              process.env.PUBLIC_URL +
                              (pos.flag || editFlagSrc(pos.code))
                            }
                            alt=""
                            width="24"
                            height="16"
                          />
                        )}
                        <Box key={pos.code}>
                          <Typography
                            key={pos.code}
                            sx={{ fontSize: "12px", color: "#6D6D6F"}}
                          >
                            {pos.name}
                          </Typography>
                          <Typography
                            key={pos.code}
                            sx={{ fontSize: "16px", color: "#000000", textAlign:'center'}}
                          >
                            {pos.iata || "-"}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: "16px", color: "#000000" }}>
                    -
                  </Typography>
                )}
                {errors.pos_items && <FieldError message={errors.pos_items} />}
              </Grid>
            </Grid>
          </Box>

          {/* ============================== */}
          {/* Section 2: Technical details */}
          {/* ============================== */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <EditSectionTitle>Technical details</EditSectionTitle>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography
                    sx={{
                      color: "#6D6D6F",
                      fontSize: "12px",
                      lineHeight: "16px",
                    }}
                  >
                    Implementation start date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                    startIcon={<img src={calendarIcon} alt=""/>}
                      value={
                        formData.implementation_start_date
                          ? moment(formData.implementation_start_date)
                          : null
                      }
                      onChange={(newValue) =>
                        setField(
                          "implementation_start_date",
                          newValue ? newValue.toISOString() : null
                        )
                      }
                      minDate={moment()}
                      open={openDatePicker}
                      onOpen={() => setOpenDatePicker(true)}
                      onClose={() => setOpenDatePicker(false)}
                      errors={errors}
                      fieldName="implementation_start_date"
                    />
                  </LocalizationProvider>
                  {errors.implementation_start_date && (
                    <FieldError message={errors.implementation_start_date} />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <CommonDropdown
                  label="Supported flow"
                  required
                  value={formData.supported_flow}
                  onChange={(v) => setField("supported_flow", v)}
                  options={supportedFlowOptions}
                  error={Boolean(errors.supported_flow)}
                  errorMessage={errors.supported_flow}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CommonDropdown
                  label="API schema"
                  required
                  value={formData.api_schema_version}
                  multiple={true}
                  onChange={(v) => setField("api_schema_version", v)}
                  options={apiSchemaOptions}
                  error={Boolean(errors.api_schema_version)}
                  errorMessage={errors.api_schema_version}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CommonDropdown
                  label="Do you have any existing NDC integration with another Airline?"
                  required
                  value={String(formData.existing_integration)}
                  onChange={(v) => setField("existing_integration", Number(v))}
                  options={[
                    { value: "1", label: "Yes" },
                    { value: "0", label: "No" },
                  ]}
                  error={Boolean(errors.existing_integration)}
                  errorMessage={errors.existing_integration}
                />
              </Grid>
              {formData.existing_integration === 1 && (
                <>
                  <Grid item xs={false} md={9} sx={{ display: { xs: "none", md: "block" } }} />
                  <Grid item xs={12} md={3}>
                    <CommonTextField
                      label="Please specify"
                      value={formData.existing_integration_comments}
                      onChange={(v) =>
                        setField("existing_integration_comments", v)
                      }
                      error={Boolean(errors.existing_integration_comments)}
                      errorMessage={errors.existing_integration_comments}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* ============================== */}
          {/* Section 3: Business & Admin contact */}
          {/* ============================== */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <EditSectionTitle>Business contact</EditSectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <CommonTextField
                      label="First name"
                      required
                      value={formData.business_contact_first_name}
                      onChange={(v) =>
                        setField("business_contact_first_name", v)
                      }
                      error={Boolean(errors.business_contact_first_name)}
                      errorMessage={errors.business_contact_first_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CommonTextField
                      label="Last name"
                      required
                      value={formData.business_contact_last_name}
                      onChange={(v) =>
                        setField("business_contact_last_name", v)
                      }
                      error={Boolean(errors.business_contact_last_name)}
                      errorMessage={errors.business_contact_last_name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CommonTextField
                      label="Phone number"
                      value={formData.business_phone_no}
                      onChange={(v) => setField("business_phone_no", v)}
                      startIcon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />}
                      error={Boolean(errors.business_phone_no)}
                      errorMessage={errors.business_phone_no}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CommonTextField
                      label="Email"
                      required
                      value={formData.business_email}
                      onChange={(v) => setField("business_email", v)}
                      startIcon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />}
                      error={Boolean(errors.business_email)}
                      errorMessage={errors.business_email}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  flexWrap="wrap"
                >
                  <EditSectionTitle sx={{ m: 0 }}>Admin contact</EditSectionTitle>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.sameAsBusiness}
                        onChange={(e) => handleSameAsBusiness(e.target.checked)}
                        sx={{
                          color: "#1460AA",
                          "&.Mui-checked": { color: "#1460AA" },
                        }}
                      />
                    }
                    label="Make Admin contact details same as Business contact details"
                    componentsProps={{
                      typography: { fontSize: "12px", color: "#000" },
                    }}
                    sx={{ m: 0, mt: "-5px" }}
                  />
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <CommonTextField
                      label="First name"
                      required
                      disabled={formData.sameAsBusiness}
                      value={formData.admin_contact_first_name}
                      onChange={(v) => setField("admin_contact_first_name", v)}
                      error={Boolean(errors.admin_contact_first_name)}
                      errorMessage={errors.admin_contact_first_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CommonTextField
                      label="Last name"
                      required
                      disabled={formData.sameAsBusiness}
                      value={formData.admin_contact_last_name}
                      onChange={(v) => setField("admin_contact_last_name", v)}
                      error={Boolean(errors.admin_contact_last_name)}
                      errorMessage={errors.admin_contact_last_name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CommonTextField
                      label="Phone number"
                      disabled={formData.sameAsBusiness}
                      value={formData.admin_contact_phone}
                      onChange={(v) => setField("admin_contact_phone", v)}
                      startIcon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />}
                      error={Boolean(errors.admin_contact_phone)}
                      errorMessage={errors.admin_contact_phone}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CommonTextField
                      label="Email"
                      required
                      disabled={formData.sameAsBusiness}
                      value={formData.admin_contact_email}
                      onChange={(v) => setField("admin_contact_email", v)}
                      startIcon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />}
                      error={Boolean(errors.admin_contact_email)}
                      errorMessage={errors.admin_contact_email}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* ============================== */}
          {/* Footer actions */}
          {/* ============================== */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setFormEdit(false)}
              sx={{
                borderRadius: "24px",
                fontWeight: 700,
                textTransform: "none",
                padding: "8px 24px",
                borderColor: "#1460AA",
                color: "#1460AA",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditFormSubmit}
              sx={{
                borderRadius: "24px",
                fontWeight: 700,
                textTransform: "none",
                padding: "8px 24px",
                backgroundColor: "#1460AA",
                "&:hover": { backgroundColor: "#1460AA" },
              }}
            >
              Save changes
            </Button>
          </Box>

          <PosModal
            open={posModalOpen}
            onClose={() => setPosModalOpen(false)}
            supList={state.registrationData?.sup || []}
            onApply={handlePosApply}
            enableIataStep={isIataEnabledForPos}
            initialSelected={formData.pos_items.map((p) => p.code ?? p.id)}
            initialIata={isIataEnabledForPos ? formData.pos_items.reduce((acc, p) => {
              acc[p.code] = p.iata || "";
              return acc;
            }, {}) : {}}
          />
        </Box>
      )
      }
      {deletePopUpOpen && (
        <DeleteSellerPopUp
          open={deletePopUpOpen}
          handleClose={() => setDeletePopUpOpen(false)}
          header="Alert"
          body="Are you sure you want to delete this seller?"
          handleBack={() => setDeletePopUpOpen(false)}
        />
      )}
    </>
  );
};

export default SellerProfile;
